/*
 * @Author: Hong.Zhang
 * @Date: 2024-12-09 13:33:31
 * @Description:
 */
import {
  EVENT_TRACKING_BUY_FOR_MAP,
  EVENT_TRACKING_INSURANCE_CATEGORY_MAP,
  EVENT_TRACKING_INSURANCE_PRODUCT_NAME,
  EVENT_TRACKING_INSURER_NAME_MAP,
} from '@/constants/eventTracking';
import { PRODUCT_NAMES } from '@/constants/product';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { SERVER_DOB_FORMATTER, formatDate } from './date';
import {
  LOCAL_FAST_QUOTE,
  LOCAL_FROM_DEEPLINK,
  LOCAL_PLAN,
  LOCAL_PROFILE,
  LOCAL_TOKEN,
  getProductName,
  local,
  productLevelLocal,
} from './store';

/**
 * Basic encapsulation module of GA
 */
function gtag(...args: any) {
  void args;
  window.dataLayer?.push(arguments);
}

export const initGA = (kredivoUserId: string) => {
  window.dataLayer = window.dataLayer || [];
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    user_id: kredivoUserId,
  });
};

const trackEvent = (eventName: string, properties?: any) => {
  gtag('event', eventName, properties);
};

/**
 * Biz logic part
 */
export const getInsuranceProductCommonInfo = () => {
  const token = local.get(LOCAL_TOKEN);
  const fromDeeplink = local.get(LOCAL_FROM_DEEPLINK) ?? false;
  const productName = getProductName();
  return {
    user_authorized: token ? 'Yes' : 'No',
    insurance_category: EVENT_TRACKING_INSURANCE_CATEGORY_MAP[productName],
    insurer_name: EVENT_TRACKING_INSURER_NAME_MAP[productName],
    insurance_product_name: EVENT_TRACKING_INSURANCE_PRODUCT_NAME[productName],
    from_deeplink: fromDeeplink ? 'Yes' : 'No',
  };
};

export const getProductRelevantData = () => {
  const productName = getProductName();
  const fastQuote = productLevelLocal.get(LOCAL_FAST_QUOTE) ?? {};
  const profile = local.get(LOCAL_PROFILE) ?? {};
  switch (productName) {
    case PRODUCT_NAMES.TRAVEL_DOMESTIC:
      return {
        journey_destination: fastQuote.destination,
        trip_start_date: formatDate(fastQuote.startDate),
        trip_end_date: formatDate(fastQuote.endDate),
        travel_as_family:
          fastQuote.adult ?? 0 + fastQuote.underage ?? 0 > 1
            ? 'Family'
            : 'Individual',
        ...(isEmpty(profile)
          ? {}
          : {
              date_of_birth: formatDate(
                dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
              ),
              address: profile.address,
              province: profile.addressProvince,
              city: profile.addressCity,
              postal_code: profile.addressPostalCode,
              gender: profile.gender,
            }),
      };
    case PRODUCT_NAMES.ZURICH_SYARIAH_PA:
    default:
      return isEmpty(profile)
        ? {}
        : {
            date_of_birth: formatDate(
              dayjs(profile.dateOfBirth, SERVER_DOB_FORMATTER),
            ),
            address: profile.address,
            province: profile.addressProvince,
            city: profile.addressCity,
            postal_code: profile.addressPostalCode,
            gender: profile.gender,
          };
  }
};

export const trackHomePageLanding = () => {
  trackEvent('Home Page - Landing');
};

export const trackContactIglooWithWA = () => {
  trackEvent('Home Page - Contact Igloo');
};

export const trackChooseProduct = () => {
  trackEvent('Home Page - Choose A Product', {
    ...getInsuranceProductCommonInfo(),
  });
};

export const trackFastQuoteLanding = () => {
  trackEvent('Fast Quote Page - Landing', {
    ...getInsuranceProductCommonInfo(),
  });
};

export const trackFastQuoteCheckPrice = () => {
  const productName = getProductName();
  trackEvent('Fast Quote Page - Check Price', {
    ...getInsuranceProductCommonInfo(),
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
  });
};

export const trackPDPLanding = () => {
  const productName = getProductName();
  trackEvent('PDP - Landing', {
    ...getInsuranceProductCommonInfo(),
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
  });
};

export const trackPDPChoosePlan = () => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('PDP - Choose A Plan', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
  });
};

export const trackApplicationFormLanding = (step: number) => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('Application Form - Landing', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    form_section_id: `Step #${step + 1}`,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
  });
};

export const trackApplicationFormSubmitInfo = (step: number, values: any) => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('Application Form - Submit Info', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    form_section_id: `Step #${step + 1}`,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
    date_of_birth: formatDate(values.dateOfBirth),
    address: values.address,
    province: values.addressProvince,
    city: values.addressCity,
    postal_code: values.addressPostalCode,
    gender: values.gender,
  });
};

export const trackSummaryLanding = () => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('Checkout Summary Page - Landing', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
  });
};

export const trackSummaryCheckDeclaration = (
  sequentialId: number,
  checked: boolean,
) => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('Checkout Summary Page - Check Declaration', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
    sequential_id: sequentialId + 1,
    check_or_not: checked ? 'Check' : 'Uncheck',
  });
};

export const trackSummaryMakePayment = () => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('Checkout Summary Page - Make Payment', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
  });
};

export const trackPaymentSuccessLanding = () => {
  const productName = getProductName();
  const plan = local.get(LOCAL_PLAN) ?? {};
  trackEvent('Payment Success Page - Landing', {
    ...getInsuranceProductCommonInfo(),
    insurance_plan_name: plan.name,
    gross_premium: plan.priceBeforeDiscount,
    discount_amount: JSON.stringify(plan.discount),
    payable_amount: plan.priceAfterDiscount,
    buy_for: EVENT_TRACKING_BUY_FOR_MAP[productName],
    ...getProductRelevantData(),
  });
};

export const trackError = (errorType: string) => {
  trackEvent('Error', {
    error_type: errorType,
  });
};
